import React from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation } from 'react-router-dom';
import { Clock } from 'lucide-mui';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { posthog } from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { setDrawerState } from '../dashboardSlice';
import { useBooleanFeature } from '../../../hooks/useFeatures';

interface HistoryButtonProps {
  open: boolean;
}

const HistoryButton: React.FC<HistoryButtonProps> = ({ open }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { getFeature } = useBooleanFeature();
  const { t } = useTranslation();

  // Check if the current route is the History page
  const isActive = location.pathname === '/history';

  const handleHistoryClick = () => {
    // Get the feature flag value
    const featureFlagValue = getFeature('feature_history_btn_top');

    // Send PostHog event with feature flag value
    posthog.capture('history-button-clicked', {
      feature_history_btn_top: featureFlagValue,
    });

    window.location.href = '/app/#/history';
    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <Tooltip
        title={t('drawer.actions.history', 'History')}
        placement="right"
        arrow
        disableHoverListener={open}
      >
        <ListItemButton
          onClick={handleHistoryClick}
          sx={{
            justifyContent: open ? 'initial' : 'center',
            px: open ? 2.5 : 1.25,
            borderRadius: 20,
            mx: 0.5,
            minHeight: '36px',
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'transparent',
            '&.Mui-selected': {
              bgcolor: 'transparent',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              backgroundColor: isActive
                ? theme.palette.action.hover
                : 'secondary.light',
              opacity: isActive ? 1 : 0,
              borderRadius: 'inherit',
            },
            '&:hover': {
              '&::before': {
                opacity: 1,
                backgroundColor: theme.palette.action.hover,
              },
            },
            '& > *': {
              position: 'relative',
              zIndex: 1,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 3 : 'auto',
              justifyContent: 'center',
              pl: open ? 0 : 0.5,
              color: isActive ? 'inherit' : 'text.secondary',
              transition: theme.transitions.create(
                ['margin', 'padding', 'color'],
                {
                  duration: 150,
                  easing: theme.transitions.easing.easeOut,
                }
              ),
              '& svg': {
                color: 'inherit',
              },
            }}
          >
            <Clock />
          </ListItemIcon>
          <ListItemText
            primary={t('drawer.actions.history', 'History')}
            sx={{
              opacity: open ? 1 : 0,
              transition: theme.transitions.create(['opacity'], {
                duration: open ? 150 : 100,
                easing: theme.transitions.easing.easeOut,
                delay: open ? 50 : 0,
              }),
              '& .MuiTypography-root': {
                fontWeight: isActive ? 700 : 400,
                color: isActive ? 'text.primary' : 'text.secondary',
              },
            }}
          />
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};

export default HistoryButton;
